import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';

import { GlobalButton, FormTextInput, FormSelectInput } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import type { SelectInputOption } from '@/components/inputs/SelectInput';
import InteractiveConfigEditor from '@/pages/Business/pages/LogisticOperator/components/ServiceRulesConfig/InteractiveConfigEditor';

import {
  ServiceVisitTypeFormValues,
  serviceVisitTypeFormSchema,
  defaultServiceVisitTypeValues,
  VisitType,
  VisitTypeLabels,
} from '../models';
import { useLogisticOperatorOptions } from '../hooks';

const typeOptions: SelectInputOption[] = [
  { label: VisitTypeLabels[VisitType.DELIVERY], value: VisitType.DELIVERY },
  { label: VisitTypeLabels[VisitType.PICKUP], value: VisitType.PICKUP },
];

type Props = {
  initialValues?: ServiceVisitTypeFormValues;
  onSubmit: (values: ServiceVisitTypeFormValues) => void;
  isSubmitting: boolean;
  submitLabel: string;
};

const ServiceVisitTypeForm = ({
  initialValues,
  onSubmit,
  isSubmitting,
  submitLabel,
}: Props) => {
  const navigate = useNavigate();
  const { options: operatorOptions, isLoading: isLoadingOperators } =
    useLogisticOperatorOptions();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<ServiceVisitTypeFormValues>({
    resolver: zodResolver(serviceVisitTypeFormSchema),
    defaultValues: initialValues ?? defaultServiceVisitTypeValues,
    mode: 'onChange',
  });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-6'>
      <div className='flex gap-2.5 justify-end'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={() => navigate(-1)}
        >
          Cancelar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.primaryButton}
          action={handleSubmit(onSubmit)}
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? 'Guardando...' : submitLabel}
        </GlobalButton>
      </div>

      <div className='w-full p-6 bg-white rounded-lg shadow-md border border-gray-100'>
        <div className='flex flex-col gap-1 mb-6 border-b border-gray-100 pb-4'>
          <h3 className='text-xl font-bold text-gray-900'>
            Información del tipo de visita
          </h3>
          <p className='text-sm text-gray-500'>
            Los tipos de visita definen si un servicio corresponde a un retiro o
            a una entrega.
          </p>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <div className='flex flex-col gap-2'>
            <FormTextInput
              name='name'
              placeholder='Nombre del tipo de visita'
              control={control}
              required
            />
          </div>

          <div className='flex flex-col gap-2'>
            <FormSelectInput
              name='type'
              placeholder='Tipo de visita'
              control={control}
              options={typeOptions}
              required
            />
          </div>

          <div className='flex flex-col gap-2'>
            <FormSelectInput
              name='logisticOperatorId'
              placeholder={
                isLoadingOperators
                  ? 'Cargando operadores...'
                  : 'Operador logístico (opcional)'
              }
              control={control}
              options={operatorOptions}
              disabled={isLoadingOperators}
            />
            <p className='text-xs text-gray-500'>
              Dejar vacío para un tipo de visita global (todos los operadores).
            </p>
          </div>

          <div className='flex flex-col gap-2 md:col-span-2'>
            <label className='text-sm font-medium text-gray-700'>
              Configuración (opcional)
            </label>
            <Controller
              name='jsonConfig'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <InteractiveConfigEditor
                  value={field.value || ''}
                  error={error?.message ?? null}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default ServiceVisitTypeForm;
